const DEVICE_ID_KEY = 'deviceId';

let cachedId: string | null = null;

/**
 * Collects stable browser/hardware characteristics that persist across sessions
 * and incognito mode. Excludes volatile data like full user-agent version strings.
 */
function collectFingerprint(): string {
  const components: string[] = [];

  components.push(
    `${window.screen.width}x${window.screen.height}x${window.screen.colorDepth}`
  );
  components.push(String(navigator.hardwareConcurrency || 0));
  components.push(navigator.language);
  components.push(navigator.platform);

  try {
    components.push(Intl.DateTimeFormat().resolvedOptions().timeZone);
  } catch {
    /* timezone unavailable */
  }

  try {
    const canvas = document.createElement('canvas');
    canvas.width = 200;
    canvas.height = 50;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.textBaseline = 'alphabetic';
      ctx.font = '14px Arial';
      ctx.fillStyle = '#f60';
      ctx.fillRect(125, 1, 62, 20);
      ctx.fillStyle = '#069';
      ctx.fillText('icardio-fp', 2, 15);
      ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
      ctx.fillText('icardio-fp', 4, 17);
      components.push(canvas.toDataURL());
    }
  } catch {
    /* canvas unavailable (test environments) */
  }

  try {
    const gl = document.createElement('canvas').getContext('webgl');
    if (gl) {
      const ext = gl.getExtension('WEBGL_debug_renderer_info');
      if (ext) {
        components.push(
          String(gl.getParameter(ext.UNMASKED_VENDOR_WEBGL)),
          String(gl.getParameter(ext.UNMASKED_RENDERER_WEBGL))
        );
      }
    }
  } catch {
    /* WebGL unavailable */
  }

  return components.join('|||');
}

async function hashToHex(input: string): Promise<string> {
  const data = new TextEncoder().encode(input);
  const buffer = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(buffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

function formatAsUUID(hex: string): string {
  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20, 32),
  ].join('-');
}

export async function getDeviceId(): Promise<string> {
  if (cachedId) return cachedId;

  const fingerprint = collectFingerprint();
  const hex = await hashToHex(fingerprint);
  const id = formatAsUUID(hex);

  cachedId = id;
  try {
    localStorage.setItem(DEVICE_ID_KEY, id);
  } catch {
    /* storage unavailable */
  }

  return id;
}
